import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { UserManager, type User } from 'oidc-client-ts';
import { oidcConfig, cognitoSignOutUrl } from './config';

interface AuthContextValue {
  user: { email: string; sub: string } | null;
  accessToken: string | null;
  loading: boolean;
  /** Set when sign-in could not even be attempted (e.g. missing/invalid
   *  Cognito config). Distinct from "not signed in yet". */
  authError: string | null;
  signOut: () => void;
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  accessToken: null,
  loading: true,
  authError: null,
  signOut: () => {},
});

const manager = new UserManager(oidcConfig);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [oidcUser, setOidcUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const existing = await manager.getUser();
        if (cancelled) return;
        if (existing && !existing.expired) {
          setOidcUser(existing);
          setLoading(false);
        } else if (window.location.pathname === '/auth/callback') {
          setLoading(false);
        } else {
          await manager.signinRedirect();
        }
      } catch (err) {
        // Without this, a misconfigured authority/client_id (e.g. an unset
        // VITE_USER_POOL_ID resolving to the literal string "undefined")
        // throws inside signinRedirect with nowhere to go: the promise
        // rejects, nothing sets loading=false, and the app just sits on a
        // blank/loading screen forever with no visible error. That exact
        // failure happened in production once already.
        if (cancelled) return;
        console.error('Auth initialization failed', err);
        setAuthError(err instanceof Error ? err.message : 'Authentication failed to initialize.');
        setLoading(false);
      }
    })();

    const onAdd = (u: User) => setOidcUser(u);
    const onRemove = () => setOidcUser(null);
    manager.events.addUserLoaded(onAdd);
    manager.events.addUserUnloaded(onRemove);
    manager.events.addAccessTokenExpired(onRemove);

    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onAdd);
      manager.events.removeUserUnloaded(onRemove);
      manager.events.removeAccessTokenExpired(onRemove);
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    return {
      user: oidcUser
        ? { email: oidcUser.profile.email ?? '', sub: oidcUser.profile.sub }
        : null,
      accessToken: oidcUser?.access_token ?? null,
      loading,
      authError,
      signOut: () => {
        const idToken = oidcUser?.id_token;
        manager.removeUser().finally(() => {
          // Build the logout URL from the same config signinRedirect used —
          // if that config is broken, signIn already failed above and surfaced
          // authError, so this path is only reached with known-good config.
          window.location.href = cognitoSignOutUrl(idToken);
        });
      },
    };
  }, [oidcUser, loading, authError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { manager as userManager };
